import { PacienteDoPedido, useDeOutraOrganizacao } from "./PacienteDoPedido";
import { useState } from "react";
import { MessageSquareText } from "lucide-react";
import { formatBRL, formatDate } from "./prosthesis-meta";
import { DENTIST_STATUS_META, dentistStatusBucket } from "./dentist-status";
import { ProsthesisOrderServiceLines } from "./ProsthesisOrderServiceLines";
import { RequestReworkDialog, type ReworkTarget } from "./RequestReworkDialog";
import type { ProsthesisOrderRow, ProsthesisServiceRow } from "./types";
import { totalParaExibir } from "@/components/prosthesis/valor-do-pedido";

export function ProsthesisOrdersCardView({
  orders,
  onOpen,
}: {
  orders: ProsthesisOrderRow[];
  onOpen: (id: string) => void;
}) {
  const deOutra = useDeOutraOrganizacao();
  const [reworkTarget, setReworkTarget] = useState<ReworkTarget>(null);

  return (
    <div className="flex flex-col gap-3">
      {orders.map((o) => (
        <OrderCard
          key={o.id}
          order={o}
          onOpen={() => onOpen(o.id)}
          onRequestRework={
            deOutra(o)
              ? undefined
              : (service) => setReworkTarget({ serviceId: service.id, serviceLabel: service.serviceCategory.label, patientName: o.patientName })
          }
        />
      ))}

      <RequestReworkDialog target={reworkTarget} onClose={() => setReworkTarget(null)} />
    </div>
  );
}

function OrderCard({
  order,
  onOpen,
  onRequestRework,
}: {
  order: ProsthesisOrderRow;
  onOpen: () => void;
  /** Sem ele (pedido de outra organização) o retrabalho não aparece no cartão. */
  onRequestRework?: (service: ProsthesisServiceRow) => void;
}) {
  const bucket = dentistStatusBucket(order.status);
  const meta = DENTIST_STATUS_META[bucket];
  const total = totalParaExibir(order.services, order.acrescimos);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{ borderLeftColor: meta.color }}
      className="w-full cursor-pointer rounded-xl border border-l-4 border-zinc-200 bg-white p-4 text-left shadow-sm transition-colors hover:border-brand-300 dark:border-border dark:bg-card"
    >
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-[1.1fr_1.6fr_1fr] sm:items-center">
        <div className="min-w-0 sm:border-r sm:border-zinc-100 sm:pr-4 dark:sm:border-border/60">
          <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">
            <PacienteDoPedido order={order} />
          </p>
          <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">{order.labName || "Sem laboratório"}</p>
          {order.dentistName && (
            <p className="truncate text-xs text-zinc-500 dark:text-muted-foreground">Dentista: {order.dentistName}</p>
          )}
          <span
            className="mt-1.5 inline-block rounded-full px-2.5 py-1 text-[11px] font-bold uppercase"
            style={{ backgroundColor: meta.bg, color: meta.color }}
          >
            {meta.label}
          </span>
        </div>

        <div className="min-w-0 text-sm sm:border-r sm:border-zinc-100 sm:px-4 dark:sm:border-border/60">
          <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">Serviços</p>
          <ProsthesisOrderServiceLines services={order.services} onRequestRework={onRequestRework} />
        </div>

        <div className="flex flex-col gap-2 sm:items-end sm:pl-4">
          <div className="flex w-full gap-4 sm:justify-end">
            <Field label="Data do pedido" value={formatDate(order.createdAt)} />
            <Field label="Prazo" value={formatDate(order.deadline)} />
          </div>
          <div className="text-right">
            <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">
              Valor total
            </p>
            <p className="text-lg font-bold tabular-nums text-zinc-900 dark:text-foreground">{formatBRL(total)}</p>
          </div>
        </div>
      </div>

      {order.description && (
        <div className="mt-3 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-300">
          <MessageSquareText className="mt-0.5 h-4 w-4 shrink-0" />
          <p>{order.description}</p>
        </div>
      )}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="w-[86px] text-right">
      <p className="text-[10px] uppercase tracking-wide text-zinc-500 dark:text-muted-foreground">{label}</p>
      <p className="text-sm font-medium text-zinc-900 dark:text-foreground">{value}</p>
    </div>
  );
}
